import { Big } from "big.js"
import { Claim } from "component/ClaimModal/type"
import { Transaction } from "container/transaction"
import { useCallback, useEffect, useMemo } from "react"
import { createContainer } from "unstated-next"
import { useImmerReducer } from "use-immer"

import { fetchHelper } from "../../../api"
import { BIG_ZERO } from "../../../constant/number"
import { useContractCall } from "../../../hook/useContractCall"
import { PerpRewardVesting } from "../../../types/contracts"
import { formatNumberWithPrecision } from "../../../util/format"
import { isEmpty } from "../../../util/isEmpty"
import { Connection } from "../../connection"
import { Contract } from "../../contract"
import { getProcessedReport, getProcessedSnapshot, getReportsHelper } from "./helper"
import { ACTIONS, initialState, LatestWeek, reducer } from "./reducer"

export const Vesting = createContainer(useVestingReward)

interface VestingContracts {
    timed: PerpRewardVesting | null
    immediately: PerpRewardVesting | null
}

export enum VestingType {
    timed = "timed",
    immediately = "immediately",
}

const vestingContractNames = [VestingType.timed, VestingType.immediately]

function useVestingReward() {
    const { signer } = Connection.useContainer()
    const { ImmediateVesting, TimedVesting } = Contract.useContainer()
    const { execute } = Transaction.useContainer()

    const [state, dispatch] = useImmerReducer(reducer, initialState)
    const { isInitialized, latestWeek } = state
    const vestingContracts: VestingContracts = useMemo(
        () => ({
            immediately: ImmediateVesting,
            timed: TimedVesting,
        }),
        [ImmediateVesting, TimedVesting],
    )

    const fetchRewards = useCallback(async () => {
        if (isInitialized) {
            return
        }

        dispatch({ type: ACTIONS.FETCH_SNAPSHOT_REQUEST })
        try {
            const snapshotUrls = {
                immediately: "/snapshot-immediate.json",
                timed: "/snapshot-vesting.json",
            }
            const snapshotArr = await Promise.all(Object.values(snapshotUrls).map(snapshot => fetchHelper(snapshot)))

            const latestWeek: LatestWeek = {}
            const snapshot: Record<string, any> = {}
            Object.keys(snapshotUrls).forEach((key, index) => {
                const { processedSnapshot, newLatestWeek } = getProcessedSnapshot(snapshotArr[index])
                snapshot[key] = processedSnapshot
                latestWeek[key] = newLatestWeek
            })

            dispatch({ type: ACTIONS.FETCH_SNAPSHOT_SUCCESS, payload: { snapshot, latestWeek } })

            dispatch({ type: ACTIONS.FETCH_REPORT_REQUEST })
            try {
                const reportArr = await getReportsHelper(snapshotArr)
                const report: Record<string, any> = {}
                Object.keys(snapshotUrls).forEach((key, index) => {
                    report[key] = getProcessedReport(reportArr[index])
                })

                dispatch({ type: ACTIONS.FETCH_REPORT_SUCCESS, payload: { report } })
            } catch {
                dispatch({ type: ACTIONS.FETCH_REPORT_FAILURE })
            }
        } catch {
            dispatch({ type: ACTIONS.FETCH_SNAPSHOT_FAILURE })
        }
    }, [dispatch, isInitialized])

    useEffect(() => {
        if (!isInitialized) {
            fetchRewards()
        }
    }, [isInitialized, fetchRewards])

    const fetchClaimStatus = useCallback(
        async (userAddress: string) => {
            dispatch({ type: ACTIONS.FETCH_CLAIMSTATUS_REQUEST })
            try {
                const claimStatusArr = await Promise.all(
                    Object.entries(vestingContracts).map(async ([key, contract]) => {
                        const ret = await contract.claimStatus(userAddress, 1, latestWeek[key])
                        return ret
                    }),
                )

                // claimStatus = { immediately: [true, false], fiveMins: [false, false] }
                const claimStatus: Record<string, boolean[]> = {}
                Object.keys(vestingContracts).forEach((key, index) => {
                    claimStatus[key] = claimStatusArr[index]
                })

                dispatch({ type: ACTIONS.FETCH_CLAIMSTATUS_SUCCESS, payload: { claimStatus } })
            } catch (err) {
                console.log(err)
                dispatch({ type: ACTIONS.FETCH_CLAIMSTATUS_FAILURE })
            }
        },
        [dispatch, latestWeek, vestingContracts],
    )

    const claimWeeks = useContractCall(
        async (userAddress: string, vestingContractName: VestingType, claims: Claim[]) => {
            const vestingContract = vestingContracts[vestingContractName]!.connect(signer)
            const receipt = await execute(vestingContract!.claimWeeks(userAddress, claims))
            await Promise.all([fetchClaimStatus(userAddress)])
            return receipt
        },
        [],
    )

    return {
        state,
        actions: {
            fetchClaimStatus,
            claimWeeks,
        },
    }
}

export interface WeeklyReward {
    amount: Big
    timestamp: number
}

export type WeeklyRewards = WeeklyReward[]

const defaultRewards = {
    isRedeemable: false,
    weeklyRewards: [],
    totalRedeemableAmount: "0.00",
    totalLockedAmount: "0.00",
    claim: () => {},
}

interface Reward {
    timestamp: number
    amount: Big
}

interface RewardList {
    [key: string]: Record<string, Reward>
}

export type Report = Record<string, string>

export function useVestingRewardByAddress(address: string) {
    const {
        state: { isInitialized, snapshot, report, claimStatus },
        actions: { fetchClaimStatus, claimWeeks },
    } = Vesting.useContainer()

    useEffect(() => {
        // check provider is initialized
        if (isInitialized) {
            fetchClaimStatus(address)
        }
    }, [address, fetchClaimStatus, isInitialized])

    return useMemo(() => {
        if (!isInitialized || !address || isEmpty(claimStatus)) {
            return defaultRewards
        }

        const now = Math.round(new Date().getTime() / 1000)

        const redeemableRewards: RewardList = {}
        const lockedRewards: RewardList = {}
        let weeklyRewards: WeeklyRewards = []

        let totalRedeemableAmount = new Big(0)
        let totalLockedAmount = new Big(0)

        vestingContractNames.forEach(key => {
            redeemableRewards[key] = {}
            lockedRewards[key] = {}
            Object.values(snapshot[key]).forEach((snapshotWeekly, idx) => {
                // claimed
                const claimStatusWeekly = claimStatus[key][idx]
                const reportWeekly: Report = report[key][idx]
                const amount = reportWeekly[address]

                // use might not have reward in this week
                // or user claimed this week before
                // claimStatus does not match the report data
                if (amount === undefined || claimStatusWeekly) {
                    return
                }

                // unclaimed
                const { timestamp, weekNumber } = snapshotWeekly
                const isRedeemable = now >= timestamp
                const reward: Reward = {
                    timestamp,
                    amount: new Big(amount),
                }
                weeklyRewards.push(reward)

                if (isRedeemable) {
                    redeemableRewards[key][weekNumber] = reward
                    totalRedeemableAmount = totalRedeemableAmount.add(reward.amount)
                } else {
                    lockedRewards[key][weekNumber] = reward
                    totalLockedAmount = totalLockedAmount.add(reward.amount)
                }
            })
        })

        weeklyRewards.sort((a, b) => {
            return a.timestamp - b.timestamp
        })

        const claim = () => {
            Object.keys(redeemableRewards).forEach(key => {
                const rewards = redeemableRewards[key]
                const weeks = Object.keys(rewards).map(x => parseInt(x))
                const vestingContractName = key
                if (weeks.length > 0) {
                    claimWeeks(address, vestingContractName as VestingType, weeks)
                }
            })
        }

        return {
            isRedeemable: totalRedeemableAmount.gt(BIG_ZERO),
            totalRedeemableAmount: formatNumberWithPrecision(totalRedeemableAmount),
            totalLockedAmount: formatNumberWithPrecision(totalLockedAmount),
            weeklyRewards,
            claim,
        }
    }, [address, claimStatus, claimWeeks, isInitialized, report, snapshot])
}
